import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { card, input, label, btnPrimary, fmtDate } from "../../ui";
import { Plus, Trash2, Calendar, Sparkles, ChevronDown, ChevronUp } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const SNAP = [
  ["main_complaint", "Queixa principal"],
  ["hypothesis", "Hipótese / diagnóstico"],
  ["treatment_plan", "Plano terapêutico"],
  ["medications", "Medicações"],
];
const LABELS = { main_complaint: "queixa", hypothesis: "hipótese", treatment_plan: "plano", medications: "medicações" };
const emptySnap = { main_complaint: "", hypothesis: "", treatment_plan: "", medications: "" };

export default function DatedView({ patient }) {
  const pid = patient.id;
  const [items, setItems] = useState([]);
  const [date, setDate] = useState(today());
  const [note, setNote] = useState("");
  const [snap, setSnap] = useState(emptySnap);
  const [showSnap, setShowSnap] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/patients/${pid}/clinical-entries`).then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, [pid]);

  const add = async () => {
    if (!note.trim() && !Object.values(snap).some((v) => v.trim()))
      return toast.error("Escreva uma anotação ou preencha o retrato clínico do dia.");
    setSaving(true);
    try {
      await api.post(`/patients/${pid}/clinical-entries`, { date, note, ...snap });
      toast.success("Registro clínico salvo.");
      setNote(""); setSnap(emptySnap); setShowSnap(false); setDate(today());
      load();
    } catch { toast.error("Erro ao salvar."); }
    finally { setSaving(false); }
  };

  const del = async (eid) => {
    if (!window.confirm("Excluir este registro clínico?")) return;
    await api.delete(`/clinical-entries/${eid}`);
    toast.success("Registro excluído.");
    load();
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
      <div className={`${card} p-6 h-fit`} data-testid="clinical-entry-form">
        <p className="font-display text-xl text-[#2C332A] mb-4">Novo registro por data</p>
        <label className={label}>Data da sessão</label>
        <input type="date" className={input} value={date} onChange={(e) => setDate(e.target.value)} data-testid="clinical-entry-date" />
        <label className={`${label} mt-4`}>Anotação clínica</label>
        <textarea rows={5} className={`${input} resize-none`} value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Observações clínicas desta data..." data-testid="clinical-entry-note" />

        <button type="button" onClick={() => setShowSnap((v) => !v)} data-testid="clinical-entry-toggle-snap"
          className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#5B6E5B] hover:text-[#2C332A]">
          {showSnap ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />} Retrato clínico do dia (opcional)
        </button>
        {showSnap && (
          <div className="mt-3 space-y-3" data-testid="clinical-entry-snapshot">
            {SNAP.map(([k, lbl]) => (
              <div key={k}>
                <label className={label}>{lbl}</label>
                <input className={input} value={snap[k]} onChange={(e) => setSnap((s) => ({ ...s, [k]: e.target.value }))} data-testid={`clinical-entry-${k}`} />
              </div>
            ))}
          </div>
        )}

        <button className={`${btnPrimary} w-full mt-4`} onClick={add} disabled={saving} data-testid="clinical-entry-add">
          <Plus className="w-4 h-4" /> Adicionar
        </button>
      </div>

      <div className="space-y-4" data-testid="clinical-entry-list">
        {items.length === 0 && <p className="font-body text-sm text-[#7A8A7A]">Nenhum registro por data ainda. Os registros aparecem aqui a cada anotação na sessão ou quando você adiciona manualmente.</p>}
        {items.map((e) => {
          const snapEntries = SNAP.filter(([k]) => (e.snapshot || {})[k]);
          return (
            <div key={e.id} className={`${card} p-5`} data-testid="clinical-entry-item">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 font-body text-sm font-medium text-[#4A5D4E]">
                  <Calendar className="w-4 h-4 text-[#C87A5E]" /> {fmtDate(e.date)}
                  {e.source === "ia" && <span className="inline-flex items-center gap-1 rounded-full bg-[#5B6E5B]/10 px-2 py-0.5 text-[11px] text-[#5B6E5B]"><Sparkles className="w-3 h-3" /> via IA</span>}
                </span>
                <button onClick={() => del(e.id)} className="text-[#C87A5E] hover:text-[#B2644D]" data-testid="clinical-entry-delete"><Trash2 className="w-4 h-4" /></button>
              </div>

              {e.note && <p className="font-body text-sm text-[#2C332A] mt-3 whitespace-pre-line leading-relaxed">{e.note}</p>}

              {snapEntries.length > 0 && (
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  {snapEntries.map(([k, lbl]) => (
                    <div key={k} className="rounded-xl bg-[#F5F0E6] px-3 py-2">
                      <p className="font-body text-[10px] tracking-[0.2em] uppercase text-[#7A8A7A]">{lbl}</p>
                      <p className="font-body text-sm text-[#2C332A] mt-0.5 whitespace-pre-line">{e.snapshot[k]}</p>
                    </div>
                  ))}
                </div>
              )}

              {e.ai_updates?.length > 0 && (
                <p className="mt-3 font-body text-[11px] text-[#8AA07E]">
                  Atualizado pela IA neste dia: {e.ai_updates.map((k) => LABELS[k] || k).join(", ")}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
